'use client';

import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon/icon';
import type { IconName } from '@/components/ui/icon/icon-registry';
import { type MessageBarShape, type MessageBarVariant } from './message-bar.model';
import styles from './message-bar.module.css';

type MessageBarProps = {
  variant?: MessageBarVariant;
  shape?: MessageBarShape;
  icon?: IconName | '';
  dismissible?: boolean;
  onDismiss?: () => void;
  /**
   * Slot before the text, after any `icon`. Exists for a leading progress
   * spinner, which reads as "this is happening now" in a way a static icon
   * cannot.
   */
  start?: ReactNode;
  end?: ReactNode;
  /**
   * Hold the bar at the height it has with a small button in `end`, even
   * while `end` is empty — for a bar whose action comes and goes (a
   * "Clear selection" shown only once something is selected), so the bar
   * and everything under it don't jump when it appears. Wrapped text can
   * still grow it past that.
   */
  reserveActionSpace?: boolean;
  /**
   * Block content under the message, inside the bar — e.g. the list of rows
   * an import rejected. `children` is a sentence (it renders in a `<p>`), so
   * a list goes here instead. Scrolling a long list is the caller's call;
   * the bar grows to fit whatever this holds.
   */
  details?: ReactNode;
  children?: ReactNode;
};

export function MessageBar({
  variant = 'info',
  shape = 'note',
  icon = '',
  dismissible = false,
  onDismiss,
  start,
  end,
  reserveActionSpace = false,
  details,
  children,
}: Readonly<MessageBarProps>) {
  const shapeClass = shape === 'wide-note' ? styles.wideNote : styles[shape];
  const reserveClass = reserveActionSpace ? ` ${styles.reserveActionSpace}` : '';
  const detailsClass = details ? ` ${styles.hasDetails}` : '';
  return (
    <div
      className={`${styles.messageBar} ${styles[variant]} ${shapeClass}${reserveClass}${detailsClass}`}
    >
      {icon ? (
        <span className={styles.messageBarIcon}>
          <Icon name={icon} size="sm" />
        </span>
      ) : null}
      {start ? <span className={styles.messageBarStart}>{start}</span> : null}
      {details ? (
        <div className={styles.messageBarBody}>
          <p className={styles.messageBarText}>{children}</p>
          <div className={styles.messageBarDetails}>{details}</div>
        </div>
      ) : (
        <p className={styles.messageBarText}>{children}</p>
      )}
      <span className={styles.messageBarEnd}>{end}</span>
      {dismissible ? (
        <Button
          size="small"
          color="secondary"
          variant="ghost"
          iconCenter="x"
          iconLabel="Dismiss"
          aria-label="Dismiss"
          onClick={() => onDismiss?.()}
        />
      ) : null}
    </div>
  );
}
